/**
 * Purpose: Star rating — read-only display (fractional fill) or interactive input.
 * Inputs: value (0–5), onChange (presence enables input mode), size ('sm'|'md'|'lg'),
 *         showValue (append numeric value), count (append review count).
 * Outputs: accessible star row; input mode renders radio-like buttons.
 */
import React from 'react';

const SIZE_CLASSES = { sm: 'h-3.5 w-3.5', md: 'h-4 w-4', lg: 'h-6 w-6' };

const Star = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.958a1 1 0 00.95.69h4.163c.969 0 1.371 1.24.588 1.81l-3.368 2.447a1 1 0 00-.363 1.118l1.286 3.958c.3.922-.755 1.688-1.539 1.118l-3.367-2.446a1 1 0 00-1.176 0l-3.367 2.446c-.783.57-1.838-.196-1.539-1.118l1.286-3.958a1 1 0 00-.363-1.118L2.062 9.385c-.783-.57-.38-1.81.588-1.81h4.163a1 1 0 00.95-.69l1.286-3.958z" />
  </svg>
);

const RatingStars = ({
  value = 0,
  onChange = null,
  size = 'md',
  showValue = false,
  count = null,
  className = '',
}) => {
  const starSize = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const numeric = Math.max(0, Math.min(5, Number(value) || 0));

  if (onChange) {
    return (
      <div className={`inline-flex items-center gap-0.5 ${className}`} role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            role="radio"
            aria-checked={numeric === star}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            onClick={() => onChange(star)}
            className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <Star
              className={`${starSize} transition-colors ${
                star <= numeric ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600 hover:text-amber-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      role="img"
      aria-label={`Rated ${numeric.toFixed(1)} out of 5`}
    >
      <span className="relative inline-flex">
        <span className="flex gap-0.5 text-gray-300 dark:text-gray-600">
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className={starSize} />
          ))}
        </span>
        <span
          className="absolute inset-0 flex gap-0.5 overflow-hidden text-amber-400"
          style={{ width: `${(numeric / 5) * 100}%` }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} className={`${starSize} flex-shrink-0`} />
          ))}
        </span>
      </span>
      {showValue && (
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{numeric.toFixed(1)}</span>
      )}
      {count != null && (
        <span className="text-sm text-gray-500 dark:text-gray-400">({count})</span>
      )}
    </span>
  );
};

export default RatingStars;
